import { Component } from "react";
import type { ReactNode, ErrorInfo } from "react";
import { captureException } from "../utils/posthog";
import { Sentry } from "../sentry";
import { isPortalTeardownRace } from "../portalTeardownRace";

interface Props {
  children: ReactNode;
  /** Identifies the boundary in monitoring (e.g. "page", "app-page"). */
  source?: string;
  /** Optional custom fallback. Receives the caught error and a reset callback. */
  fallback?: (error: Error, reset: () => void) => ReactNode;
}

interface State {
  error: Error | null;
}

/**
 * App-wide error boundary. A render error in any wrapped subtree is caught here
 * instead of bubbling to Wasp Router's default boundary (which blanks the whole
 * page with no telemetry). Every caught error is reported to BOTH PostHog and
 * Sentry with the React component stack, so render crashes become diagnosable
 * instead of silent.
 *
 * Note: async errors thrown from outside React's render (e.g. a ResizeObserver
 * callback) are NOT caught here — those are handled by installChartErrorGuard.
 *
 * The default fallback is intentionally provider-free (no i18n hooks, no
 * context) so it can never itself throw — a fallback that depends on the very
 * providers that may have crashed would defeat the purpose.
 */
export class AppErrorBoundary extends Component<Props, State> {
  state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    // Swallow the benign portal race — keep rendering children instead of
    // showing the fallback. Anything else takes the normal fallback path.
    if (isPortalTeardownRace(error)) return { error: null };
    return { error };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    // La portal-teardown race est neutralisée à l'affichage par
    // getDerivedStateFromError, mais elle reste rapportée ici DÉLIBÉRÉMENT :
    // la cause a été traitée (portails isolés dans #edgely-portal-root, voir
    // portalContainer.ts) et c'est ce signal qui permettra de voir si elle
    // réapparaît. Filtrer la télémétrie rendrait une régression invisible.
    const source = this.props.source ?? "AppErrorBoundary";
    // eslint-disable-next-line no-console
    console.error(`[${source}]`, error, info);
    const context = { componentStack: info.componentStack, source };
    try {
      captureException(error, context);
    } catch {
      /* never let reporting throw */
    }
    try {
      Sentry.captureException(error, { extra: context });
    } catch {
      /* never let reporting throw */
    }
  }

  reset = () => this.setState({ error: null });

  render() {
    const { error } = this.state;
    if (!error) return this.props.children;

    if (this.props.fallback) return this.props.fallback(error, this.reset);

    // Tailwind token classes only (statically compiled, so they render even if
    // the crash came from a runtime provider). No hardcoded hex — stays within
    // the theme-token guardrail (themeColors.test.ts).
    return (
      <div
        role="alert"
        className="mx-auto mt-[15vh] max-w-[480px] px-6 text-center text-muted-foreground"
      >
        <p className="mb-4 text-[15px] text-foreground">
          Une erreur est survenue lors de l'affichage de cette page. / Something
          went wrong while displaying this page.
        </p>
        <button
          type="button"
          onClick={() => window.location.reload()}
          className="rounded-lg border border-border bg-card px-4 py-2 text-sm text-foreground hover:bg-accent"
        >
          Recharger / Reload
        </button>
      </div>
    );
  }
}

export default AppErrorBoundary;
